// Maps a card-section grid `type` to a Next <Image> `sizes` string.
//
// Why this exists: <Image> only builds a width-descriptor srcset (so the
// browser can pick a right-sized file per viewport) when it's given `sizes`.
// Without it, a non-`fill` image falls back to density descriptors (1x/2x)
// derived from the *intrinsic* width — which, against our 1200px deviceSizes
// cap (next.config.ts), collapses to w=1200 for every device. That ships a
// 1200px-wide file to a 390px phone. Passing `sizes` that reflects each
// layout's rendered width fixes the over-fetch.
//
// Widths are deliberately approximate — the goal is right-ballpark bytes, not
// pixel-perfect — and assume the ~1200px max container.

type GridType = string | undefined;

/** The catch-all default: a 3-up grid (sm 1-col, md 2-col, lg 3-col). */
export const DEFAULT_CARD_IMAGE_SIZES =
  "(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 400px";

/**
 * A single full-container card (hero, 1-up). The hero box maxes out at ~1184px
 * CSS (`.heroCard` is 1280px − padding). We declare ~1280px on desktop so a 1x
 * screen pulls a right-sized file while a 2x-retina screen (≈2368px needed)
 * reaches the 2400px srcset bucket — that bucket (in `deviceSizes`,
 * next.config.ts) is what keeps the hero sharp on retina. Below 1280px the
 * hero is full-bleed → 100vw.
 */
export const HERO_IMAGE_SIZES = "(max-width: 1280px) 100vw, 1280px";

/**
 * Encode quality for the hero (LCP) image. Slightly above the default 75 to
 * offset the Next-over-Cloudinary double compression, but well below the old
 * 90 — at 90 the full-bleed portrait hero was ~2x the bytes, the bulk of the
 * "Improve image delivery (83 KiB)" over-delivery that helped push mobile LCP
 * to 7.5s on Slow 4G. 80 is visually indistinguishable here and roughly halves
 * the transfer. Must be listed in `images.qualities` (next.config.ts).
 */
export const HERO_IMAGE_QUALITY = 80;

/** Showcase media column (~60% on desktop, full width when stacked). */
export const SHOWCASE_IMAGE_SIZES = "(max-width: 1024px) 100vw, 720px";

export function cardImageSizes(type?: GridType): string {
  switch (type) {
    case "type_4up":
      // sm/md 2-col, lg 4-col within ~1200px → ~300px on desktop.
      return "(max-width: 1024px) 50vw, 300px";
    case "type_2up50/50":
      // sm 1-col, md/lg 2-col → ~600px on desktop.
      return "(max-width: 768px) 100vw, 600px";
    case "type_2up60/40":
    case "type_2up40/60":
      // sm 1-col, md+ split — the wider column tops out ~720px.
      return "(max-width: 768px) 100vw, 720px";
    case "type_1up":
    case "type_1up100vh":
      return HERO_IMAGE_SIZES;
    case "type_showcaseLeft":
    case "type_showcaseRight":
      return SHOWCASE_IMAGE_SIZES;
    case "type_3up":
    case "type_3upCenter":
    default:
      return DEFAULT_CARD_IMAGE_SIZES;
  }
}
